import { useNavigate, useParams } from "@solidjs/router";
import { createEffect, createResource, createSignal, For, onCleanup, Show } from "solid-js";
import type { PaymentMethodView, TripSummary } from "../../shared/api";
import { api } from "../api";
import { ReportChoiceSheet } from "../components/ReportSheets";
import { reportHref } from "../report";
import { tripPlan } from "../state/tripPlan";
import { keepOnFailedRefresh } from "../resource";
import { AppBar } from "../components/AppBar";
import { Button } from "../components/Button";
import { ComingSoonToast } from "../components/ComingSoon";
import { ScreenLoader } from "../components/Spinner";
import { billedLine } from "../../shared/rate";
import { distanceLabel, euros, minutes } from "../format";

/** Figma 08 Ride summary: locked, stats, rating (local only), payment lines, hold released. */
export function Trip() {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  // Keyed on the id: going from one recap to another loads the new trip.
  const [summary, { refetch }] = createResource(
    () => params.id,
    keepOnFailedRefresh((id: string) => api.get<TripSummary>(`/trips/${encodeURIComponent(id)}`)),
  );
  // Pony computes the distance a few minutes after the end: refresh until it shows up (max ~5 min).
  let polls = 0;
  const [gaveUp, setGaveUp] = createSignal(false);
  const timer = setInterval(() => {
    if (summary.state !== "ready" || !summary()?.distancePending) return;
    if (polls++ < 30) void refetch();
    else setGaveUp(true);
  }, 10_000);
  onCleanup(() => clearInterval(timer));
  // The ride of a followed itinerary is over: stop following it (no banner on the next booking).
  createEffect(() => {
    const s = summary.state === "ready" ? summary() : undefined;
    if (s && tripPlan.followedFor(s.vehicleId)) tripPlan.follow(undefined);
  });
  const [rating, setRating] = createSignal<number>();
  const [reportOpen, setReportOpen] = createSignal(false);
  const [method] = createResource(() =>
    api.get<PaymentMethodView | null>("/payments/method").catch(() => null),
  );

  return (
    <main class="screen bg-surface">
      <AppBar title="Trajet terminé" back="/map" close />
      <Show
        when={summary()}
        fallback={
          <Show when={summary.error} fallback={<ScreenLoader label="Chargement du trajet…" />}>
            <p class="p-6 text-center text-text">Trajet introuvable.</p>
          </Show>
        }
      >
        {(s) => (
          <div class="screen-body px-[15px] pt-4 pb-[max(50px,env(safe-area-inset-bottom))]">
            <img src="/icons/check-circle-56.svg" width="56" height="56" alt="" class="mx-auto" />
            <h1 class="mt-[6px] text-center text-[21px] font-medium leading-[1.25] tracking-[0.21px] text-violet">
              {s().state === "CANCELLED" ? "Trajet annulé" : "Merci, à bientôt !"}
            </h1>
            <p class="mt-1 text-center text-sm leading-[1.25] tracking-[0.14px] text-text">
              {s().vehicleId}
              {s().parkingName
                ? ` · ${(s().parkingName ?? "").replace(/^Stationnement/, "stationnement")}`
                : ""}
            </p>
            {/* Figma 08 stats card: lilac, three columns icon / value / label. */}
            <div class="mt-[14px] grid h-[70px] grid-cols-3 rounded-[12px] bg-surface2 pt-3 text-center">
              <SummaryStat
                icon="/icons/clock-20.svg"
                value={minutes(s().durationSec)}
                label="Durée"
              />
              <SummaryStat
                icon="/icons/bike-indigo-20.svg"
                value={distanceLabel(s(), gaveUp())}
                label="Distance"
              />
              <SummaryStat
                icon="/icons/parking-violet-20.svg"
                value={s().state === "FINISHED" ? parkingLabel(s().parking) : "—"}
                label="Stationnement"
              />
            </div>
            <h2 class="mt-[18px] ml-[5px] text-[15px] font-medium leading-[1.25] tracking-[0.15px] text-strong">
              Bon trajet ?
            </h2>
            <div class="mt-[5px] flex justify-between px-[19px]">
              <For each={[1, 2, 3, 4, 5] as const}>
                {(n) => (
                  <button
                    type="button"
                    aria-label={`${n} sur 5`}
                    aria-pressed={rating() === n}
                    class="relative flex h-11 w-11 items-center justify-center"
                    onClick={() => setRating(n)}
                  >
                    <Show when={rating() === n}>
                      <img
                        src="/icons/face-selected-circle.svg"
                        width="44"
                        height="44"
                        alt=""
                        class="absolute inset-0"
                      />
                    </Show>
                    <img
                      src={n === 5 ? "/icons/face-5-selected.svg" : `/icons/face-${n}.svg`}
                      width="36"
                      height="36"
                      alt=""
                      class="relative"
                      style={{ filter: faceFilter(n, rating() === n) }}
                    />
                  </button>
                )}
              </For>
            </div>
            <button
              type="button"
              class="mt-1 ml-[5px] h-8 self-start text-[13px] leading-[1.25] tracking-[0.13px] text-link underline"
              onClick={() => setReportOpen(true)}
            >
              J'ai eu un problème
            </button>
            {/* Figma R1: what kind of problem, then the R2–R6 flow. */}
            <ReportChoiceSheet
              open={reportOpen()}
              onClose={() => setReportOpen(false)}
              href={reportHref({ vehicle: s().vehicleId, trip: s().id, back: `/trip/${s().id}` })}
            />
            {/* Figma 08 payment card, then the link to its receipt (H3). */}
            <div class="mt-[2px] rounded-[12px] border border-border bg-white px-4 pt-[14px] pb-4">
              <div class="text-lg font-medium leading-[1.25] tracking-[0.18px] text-strong">
                Paiement
              </div>
              <div class="mt-3 flex justify-between text-sm leading-[1.25] tracking-[0.14px] text-text">
                <span>Déverrouillage</span>
                <span>{s().unlockCents === 0 ? "Offert" : euros(s().unlockCents)}</span>
              </div>
              <div class="mt-2 flex justify-between gap-3 text-sm leading-[1.25] tracking-[0.14px] text-text">
                <span>{billedLine(s().billedMinutes, s().priceLabel)}</span>
                <span class="shrink-0">{euros(Math.max(0, s().priceCents - s().unlockCents))}</span>
              </div>
              <div class="mt-[14px] border-t border-border pt-[11px] flex justify-between text-base font-semibold leading-[1.25] tracking-[0.16px] text-strong">
                <span>Total</span>
                <span>{euros(s().priceCents)}</span>
              </div>
              <div class="mt-[6px] text-xs leading-[1.25] tracking-[0.12px] text-text">
                {s().priceCents > 0
                  ? `Payé ${method()?.wallet === "apple_pay" ? "avec Apple Pay" : "par carte"} · empreinte de ${euros(s().holdCents)} libérée`
                  : `Empreinte de ${euros(s().holdCents)} libérée, rien à payer`}
              </div>
            </div>
            <Show when={s().state === "FINISHED"}>
              <a
                href={`/historique/${encodeURIComponent(s().id)}`}
                class="mt-[10px] block text-center text-sm leading-[1.25] tracking-[0.14px] text-link underline"
              >
                Reçu disponible dans Historique d'achat
              </a>
            </Show>
            <div class="mt-auto shrink-0 pt-6">
              <Button onClick={() => navigate("/map", { replace: true })}>Terminer</Button>
            </div>
          </div>
        )}
      </Show>
      <ComingSoonToast />
    </main>
  );
}

/** Figma 08 stats-card column: 20 px icon, Medium 15 value, Regular 11 label. */
function SummaryStat(props: { icon: string; value: string; label: string }) {
  return (
    <div class="flex flex-col items-center">
      <img src={props.icon} width="20" height="20" alt="" />
      <div class="mt-[2px] text-[15px] font-medium leading-[1.25] tracking-[0.15px] text-strong">
        {props.value}
      </div>
      <div class="text-[11px] leading-[1.25] tracking-[0.11px] text-text">{props.label}</div>
    </div>
  );
}

/**
 * Only the grey faces 1–4 and the white face 5 exist as assets: recolour the others
 * (white on the violet disc, #cecece = 0.808 grey for an unselected face 5).
 */
const faceFilter = (n: number, selected: boolean) =>
  selected
    ? n === 5
      ? undefined
      : "brightness(0) invert(1)"
    : n === 5
      ? "brightness(0) invert(0.808)"
      : undefined;

/** START_FINISHING verdict → recap label. */
function parkingLabel(verdict: string | undefined): string {
  switch (verdict) {
    case undefined:
    case "TAKE_PICTURE":
      return "Validé";
    case "NOT_IN_PARKING_ZONE":
      return "Hors zone";
    case "PARKING_NOT_FOR_THIS_VEHICLE_TYPE":
      return "Non adapté";
    case "JAUGE_FAILED":
      return "Parking plein";
    default:
      return "Validé";
  }
}
